import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import * as Yup from "yup";
import {
  formatToCurrency,
  getCurrencySymbol,
  parseStringToNumber,
} from "../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import {
  useGetCooperativeWalletPaystackPaymentMutation,
  useVerifyCooperativeWalletPaymentMutation,
} from "../../../../redux/features/finances/finances.api";
import { useAppSelector } from "../../../../redux/hooks";
import { payStackPopup } from "../../../../utils/constants";
import { TextInput } from "../../../inputs/textInput";
import { PaymentSelectionModal } from "../paymentSelectionModal";
import { SuccessModal } from "../successModal";

export const FundCooperativeWalletModal = ({
  modal,
  modalOpen,
  handleModalClose,
  handleModalOpen,
  cooperativeDetails,
  onSuccess,
}: {
  modalOpen: boolean;
  handleModalClose: () => void;
  modal: string;
  handleModalOpen: (name: string) => void;
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
}) => {
  const { profile } = useAppSelector((state) => state.auth);

  const [getPaystackPayment, { isLoading: fetchingPaystackLink }] =
    useGetCooperativeWalletPaystackPaymentMutation();

  const [verifyPayment] = useVerifyCooperativeWalletPaymentMutation();

  const reset = () => {
    resetForm();
    handleModalClose();
  };

  const {
    values,
    handleBlur,
    errors,
    touched,
    handleChange,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      amount: "",
    },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      amount: Yup.string().required("Amount is required"),
    }),
    onSubmit: () => {},
  });

  return (
    <>
      <PaymentSelectionModal
        isModalOpen={modalOpen && modal === "fundCooperativeWalletModal"}
        onModalClose={() => {
          reset();
        }}
        title="How much do you want to fund?"
        canProccedToPaymentMethod={isValid}
        fetchingPayment={fetchingPaystackLink}
        onMethodSelect={async (method, setMethod) => {
          if (method === "paystack") {
            const resp = await getPaystackPayment({
              id: cooperativeDetails.id,
              coop: cooperativeDetails.id,
              email: profile?.Email ?? "",
              amount: parseStringToNumber(values.amount) ?? 0,
              type: "coopwallet",
            });

            if (resp.data?.success) {
              payStackPopup.resumeTransaction(resp.data.data.data.access_code, {
                onSuccess: async (successData) => {
                  handleModalOpen("fundWalletSuccessModal");

                  await verifyPayment({
                    coop: cooperativeDetails.id,
                    trxref: successData.trxref,
                    amount: parseStringToNumber(values.amount) ?? 0,
                    reference: successData.reference,
                    mode: "paystack",
                  });

                  onSuccess();
                },
                onError: () => {
                  reset();
                },
                onCancel: () => {
                  reset();
                },
              });
            } else {
              setMethod(undefined);
            }
          }
        }}
      >
        <NumericFormat
          suffix={undefined}
          prefix={getCurrencySymbol("NGN")}
          decimalScale={2}
          thousandSeparator
          allowLeadingZeros={false}
          customInput={TextInput}
          onBlur={handleBlur}
          onChange={handleChange}
          type="text"
          name={"amount"}
          value={values?.amount ?? ""}
          placeholder="Enter amount"
          hasIcon={false}
          errors={errors?.amount && touched?.amount ? errors?.amount : ""}
          inputClass=""
          className=""
          customIconPosition="left"
        />
      </PaymentSelectionModal>

      <SuccessModal
        message={"Account funded successfully"}
        subText={
          <p>
            You have successfully funded{" "}
            <span className="text-pry-green-200 font-semibold">
              {cooperativeDetails.CoopName}
            </span>{" "}
            with{" "}
            <span className="text-pry-green-200 font-semibold">
              {formatToCurrency(values?.amount, {
                currency: "NGN",
                decimalPlaces: 2,
              })}
            </span>
          </p>
        }
        isModalOpen={modalOpen && modal === "fundWalletSuccessModal"}
        onModalClose={() => {
          reset();
        }}
      />
    </>
  );
};
